import React from 'react'
import ConfiguratorAppContext from '../ConfiguratorAppContext'
import Button from './Button'

import './Header.scss'
import { IconList, IconWallColor } from './icons'

import { BuildConfig } from '../config/BuildConfig'
import { DropdownTabItem } from './DropdownTabItem'
import { getUserTypeDisplayName, getUserTypeIcon, UserType } from '../types/userTypes'
import { AppStateActions } from '../state/AppState'
import SelectUserTypePopup from '../list/SelectUserTypePopup'
import ConfirmUserTypePopup from '../list/ConfirmUserTypePopup'
import { ResponsiveIconButton } from './ResponsiveIconButton'
import { ResponsiveBrandLogo } from './ResponsiveBrandLogo'
import { ENABLE_ELECTRICIAN_VIEW } from '../environment'
import { AnalyticsContext } from '../analytics/AnalyticsContext'

type UserTypeSelection = { nextUserType: UserType | null } | null

interface Props {
  isUserTypeSelectorEnabled: boolean
}

const Header = ({ isUserTypeSelectorEnabled }: Props) => {
  const arrowSize = 13
  const context = React.useContext(ConfiguratorAppContext)
  const analyticsLogger = React.useContext(AnalyticsContext)

  const { sharingListShown, sharingBomShown, listShown, wallColorShown } = context

  const [userTypeSelection, setUserTypeSelection] = React.useState<UserTypeSelection>(null)

  const handleSelectUserType: (userType: UserType) => void = (userType: UserType) => {
    setUserTypeSelection({ nextUserType: userType })
  }

  const showBomButton: boolean = context.userType === UserType.Electrician && ENABLE_ELECTRICIAN_VIEW

  const handleConfirmUserType = (newUserType: UserType) => {
    context.dispatch({ action: AppStateActions.SelectUserType, userType: newUserType })
    setUserTypeSelection(null)
  }

  const shouldShowUserTypeSelector = context.userTypeSelectionEnabled

  return (
    <div className="Header">
      <ResponsiveBrandLogo brand={BuildConfig.brand} />

      <DropdownTabItem
        selected={wallColorShown}
        arrowSize={arrowSize}
        forceHide={sharingBomShown || listShown || sharingListShown}
        controlColor
      >
        <ResponsiveIconButton
          iconSrc={IconWallColor}
          iconSize={20}
          buttonStyle="grey"
          onClick={context.onClickWallColor}
          label="Wall Colour"
        />
      </DropdownTabItem>
      {shouldShowUserTypeSelector && (
        <ResponsiveIconButton
          disabled={!isUserTypeSelectorEnabled}
          buttonStyle="grey"
          onClick={() => {
            analyticsLogger.log({
              event: 'cta_click',
              cta_name: getUserTypeDisplayName(context.userType || UserType.Consumer) ?? 'User type',
              cta_link: null,
              cta_category: 'Button',
              cta_location: 'Header'
            })

            setUserTypeSelection({ nextUserType: null })
          }}
          style={{
            marginLeft: '10px',
            // show in front of user type popup
            zIndex: userTypeSelection ? 1000000 : undefined,
          }}
          iconSrc={getUserTypeIcon(context.userType || UserType.Consumer)}
          label={getUserTypeDisplayName(context.userType || UserType.Consumer)}
          color="black"
        />
      )}
      {userTypeSelection && !userTypeSelection.nextUserType && (
        <SelectUserTypePopup
          userType={context.userType}
          onConfirm={ENABLE_ELECTRICIAN_VIEW ? handleSelectUserType : (userType: UserType) => handleConfirmUserType(userType)}
          onClose={() => { setUserTypeSelection(null) }}
          inline
        />
      )}
      {userTypeSelection?.nextUserType && ENABLE_ELECTRICIAN_VIEW && (
        <ConfirmUserTypePopup
          selectedUserType={userTypeSelection.nextUserType}
          onConfirm={() => handleConfirmUserType(userTypeSelection?.nextUserType || UserType.Consumer)}
          onClose={() => { setUserTypeSelection(null) }}
        />
      )}
      <div style={{ flex: '1 1 0' }} />
      <div className="listButtonContainer" style={{ transform: context.listAccessible ? undefined : 'translateX(200%)' }}>
        <Button buttonStyle={showBomButton ? 'greenSegmentedLeft' : 'green'} onClick={context.onClickList} isActive={listShown}>
          <img className="iconLeft" src={IconList} alt="" width="16" />
          My List
        </Button>
        {showBomButton && (
          <Button buttonStyle="greenSegmentedRight" onClick={context.onClickShareBom} isActive={sharingBomShown}>
            BOM
          </Button>
        )}
      </div>
    </div>
  )
}

export default Header
